import { useLayoutEffect, useRef, useState } from 'react'
import { candidateSections, candidateUpdate, useProfile, type ProfileData } from '@/hooks/use-profile'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { PhoneContactField } from '@/components/ui/social-contact-field'
import { EntityLocationFields } from '@/components/ui/entity-location-fields'
import { EntityPublicContactFields, entityPublicContactFrom } from '@/components/ui/entity-public-contact-fields'
import { extraSocialFieldsFrom } from '@/lib/social-networks'
import { CLIENT_LOCALE } from '@/lib/client-locale'
import { entityCountryForEdit } from '@/utils/entity-location-fields'
import { prepareWebsiteForSave } from '@/lib/normalize-website-url'
import { shallowDraftEqual } from '@/utils/draft-equality'
import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasInlineError,
  ArtifactCanvasSaveActions,
} from '@/components/artifact/artifact-canvas-layout'
import { OPEN_TO_WORK_BADGE_LABEL } from '@/components/profile/open-to-work-badge'

type DomainDraft = {
  headline: string
  city: string
  country: string
  phone: string
  website: string
  linkedin_url: string
  x_url: string
  open_to_work: boolean
} & ReturnType<typeof extraSocialFieldsFrom> & { extra_social_order: string[] }

function buildDomainDraft(profile: ProfileData): DomainDraft {
  return {
    headline: profile.headline ?? '',
    city: profile.city ?? '',
    country: entityCountryForEdit(profile.country, CLIENT_LOCALE),
    phone: profile.phone ?? '',
    open_to_work: candidateSections(profile).open_to_work === true,
    ...entityPublicContactFrom(profile),
  }
}

interface PersonalInformationCanvasProps {
  profile: ProfileData
  onClose: () => void
  /** When false, hide professional headline field (light profile). Default true. */
  showHeadline?: boolean
  /** When false, hide website and social fields (light profile). Default true. */
  showPublicLinks?: boolean
  /** When true, show Open to work checkbox. Default false. */
  showOpenToWork?: boolean
}

export function PersonalInformationCanvas({
  profile,
  onClose,
  showHeadline = true,
  showPublicLinks = true,
  showOpenToWork = false,
}: PersonalInformationCanvasProps) {
  const { updateProfile, isUpdating, updateError } = useProfile()

  const didHydrateDraftRef = useRef(false)
  const [domainDraft, setDomainDraft] = useState<DomainDraft>(() => buildDomainDraft(profile))
  const [domainBaseline, setDomainBaseline] = useState<DomainDraft>(() => buildDomainDraft(profile))
  const [validationError, setValidationError] = useState<string | null>(null)

  const profileId = profile.id ?? profile.email ?? 'profile'

  useLayoutEffect(() => {
    didHydrateDraftRef.current = false
  }, [profileId])

  useLayoutEffect(() => {
    if (didHydrateDraftRef.current) return
    didHydrateDraftRef.current = true
    const nextDomain = buildDomainDraft(profile)
    setDomainDraft(nextDomain)
    setDomainBaseline(nextDomain)
  }, [profile, profileId])

  const isDirty = !shallowDraftEqual(domainDraft, domainBaseline)

  const handleSave = async () => {
    const { website, error: websiteErr } = prepareWebsiteForSave(domainDraft.website)
    if (websiteErr) {
      setValidationError(websiteErr)
      return
    }

    setValidationError(null)

    if (isDirty) {
      await updateProfile({
        headline: domainDraft.headline,
        city: domainDraft.city,
        country: domainDraft.country,
        phone: domainDraft.phone,
        website,
        linkedin_url: domainDraft.linkedin_url,
        x_url: domainDraft.x_url,
        ...(showOpenToWork ? candidateUpdate({ open_to_work: domainDraft.open_to_work }) : {}),
        ...extraSocialFieldsFrom(domainDraft),
        extra_social_order: domainDraft.extra_social_order,
      })
      setDomainBaseline(domainDraft)
    }

    onClose()
  }

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <form
        className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6"
        onSubmit={(event) => event.preventDefault()}
      >
        <p className="text-sm text-muted-foreground">
          Your photo and name are managed in account settings.
        </p>

        {showHeadline ? (
          <FormField label="Current or target role" htmlFor="artifact-profile-headline">
            <Input
              id="artifact-profile-headline"
              value={domainDraft.headline}
              onChange={(event) => {
                setDomainDraft((current) => ({ ...current, headline: event.target.value }))
              }}
              placeholder="e.g. Senior Engineer, or aspiring Product Manager"
              autoComplete="organization-title"
              disabled={isUpdating}
            />
          </FormField>
        ) : null}

        <EntityLocationFields
          idPrefix="artifact-profile"
          value={{ city: domainDraft.city, country: domainDraft.country }}
          onChange={(patch) => setDomainDraft((current) => ({ ...current, ...patch }))}
          disabled={isUpdating}
        />

        <PhoneContactField
          id="artifact-profile-phone"
          value={domainDraft.phone}
          onChange={(phone) => setDomainDraft((current) => ({ ...current, phone }))}
          disabled={isUpdating}
        />

        {showPublicLinks ? (
          <EntityPublicContactFields
            idPrefix="artifact-profile"
            value={entityPublicContactFrom(domainDraft)}
            onChange={(patch) => setDomainDraft((current) => ({ ...current, ...patch }))}
            disabled={isUpdating}
          />
        ) : null}

        {showOpenToWork ? (
          <div className="flex items-start gap-3">
            <Checkbox
              id="artifact-profile-open-to-work"
              className="mt-0.5"
              checked={domainDraft.open_to_work}
              disabled={isUpdating}
              onCheckedChange={(checked) => {
                setDomainDraft((current) => ({
                  ...current,
                  open_to_work: checked === true,
                }))
              }}
            />
            <div className="space-y-1">
              <Label htmlFor="artifact-profile-open-to-work" className="cursor-pointer font-medium">
                {OPEN_TO_WORK_BADGE_LABEL}
              </Label>
              <p className="text-sm text-muted-foreground">
                Recruiters can find you when they filter for people who are open to work.
              </p>
            </div>
          </div>
        ) : null}

        <ArtifactCanvasInlineError>
          {validationError ||
            (updateError
              ? updateError instanceof Error
                ? updateError.message
                : 'Failed to save profile'
              : null)}
        </ArtifactCanvasInlineError>
      </form>

      <ArtifactCanvasFooterBar
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={handleSave}
            isDirty={isDirty}
            isSaving={isUpdating}
            closeDisabled={isUpdating}
          />
        }
      />
    </ArtifactCanvasBody>
  )
}
